import { useEffect, useRef, useState } from 'react';
import type { FC, SyntheticEvent } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { CustomBranding, mapCustomBranding } from '../../models/User';
import { useAppSelector } from '../../redux/hooks';
import { selectedUnit } from '../../redux/reducers/unitSlice';
import { selectUser } from '../../redux/reducers/userSlice';
import {
  resolveUnitBaseColor,
  unitHeaderBadgeColors,
} from '../../businessLogicService/StudentDashboardAux/getColorByUnit';
import { UnitHeaderFoundationsIcon } from './DashboardUnitHeaderIcons/UnitHeaderFoundationsIcon';
import { UnitHeaderSupportIcon } from './DashboardUnitHeaderIcons/UnitHeaderSupportIcon';
import { UnitHeaderTargetIcon } from './DashboardUnitHeaderIcons/UnitHeaderTargetIcon';
import { UnitHeaderExitSlipIcon } from './DashboardUnitHeaderIcons/UnitHeaderExitSlipIcon';

type BadgeVariant = 'foundations' | 'support' | 'target' | 'exit-slip';

interface Props {
  variant: BadgeVariant;
  // Foundations: whether every foundation skill is complete. The badge is hidden until then.
  isActive?: boolean;
  // Support / Target / Exit Slip: number of completed skills, overlaid on the badge's bubble.
  count?: number;
}

const badgeSizeClassName = 'tw-h-16 tw-w-auto';

const EXIT_SLIP_TOOLTIP_ID = 'unit-header-exit-slip-tooltip';

// Renders one of the designed unit-header badge SVGs (DashboardUnitHeaderIcons/*), tinted with the
// selected unit's colors. Foundations only appears once complete and Exit Slip only for Miami-branded
// networks; Support/Target are always shown. Counted badges are muted (grayscale + reduced opacity)
// while their count is 0 and in full unit color once above 0, with the real count overlaid on the
// badge's bubble.
//
// Exit Slip alone is interactive: its number needs saying out loud — it counts exit tickets *done*,
// not passed, and not across the year but within this unit — so it carries the design's ClickDetail
// popover. The other three sit beside skill counts the page already spells out.
export const UnitSkillBadge: FC<Props> = ({ variant, isActive, count }) => {
  const unit = useAppSelector(selectedUnit);
  const userRedux = useAppSelector(selectUser);
  const tooltip = useRef<OverlayPanel>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const colors = unitHeaderBadgeColors[resolveUnitBaseColor(unit?.name)];
  const miamiBrand =
    userRedux?.network?.customBranding &&
    mapCustomBranding[userRedux?.network?.customBranding] === CustomBranding.MIAMI;

  useEffect(() => {
    if (!tooltipOpen) return;
    let frame = 0;
    const realign = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => tooltip.current?.align());
    };
    window.addEventListener('scroll', realign, { passive: true, capture: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', realign, { capture: true });
    };
  }, [tooltipOpen]);

  if (variant === 'foundations') {
    if (!isActive) return null;
    return <UnitHeaderFoundationsIcon className={badgeSizeClassName} colors={colors} />;
  }

  // Exit Slips only exist in the Miami-branded experience, so the badge is hidden elsewhere.
  if (variant === 'exit-slip' && !miamiBrand) return null;

  const BadgeIcon =
    variant === 'support'
      ? UnitHeaderSupportIcon
      : variant === 'target'
      ? UnitHeaderTargetIcon
      : UnitHeaderExitSlipIcon;
  const muted = (count ?? 0) === 0;

  // When muted, the container is only faded (opacity) so the central multicolor icon keeps its color;
  // the grayscale is applied to the unit-colored chrome (inside the SVG) and to the count text.
  const containerClassName = `tw-relative tw-inline-flex ${muted ? 'tw-opacity-80' : ''}`;
  const badgeBody = (
    <>
      <BadgeIcon className={badgeSizeClassName} colors={colors} muted={muted} />
      <span
        className={`tw-absolute tw-left-1/2 tw-top-[82%] tw-translate-x-[-50%] tw-translate-y-[-50%] tw-text-sm tw-font-bold tw-leading-none tw-font-['Atkinson_Hyperlegible'] ${
          colors.count
        } ${muted ? 'tw-grayscale' : ''}`}
      >
        {count}
      </span>
    </>
  );

  if (variant !== 'exit-slip') {
    return <div className={containerClassName}>{badgeBody}</div>;
  }

  return (
    <>
      {/* A real button, not a div with a handler: the popover is the only place the number is
          explained, so it has to be reachable by keyboard and announced as expandable. The label
          carries the count because the popover's own copy is static and says nothing on its own. */}
      <button
        type="button"
        onClick={(event: SyntheticEvent) => tooltip.current?.toggle(event)}
        aria-expanded={tooltipOpen}
        aria-controls={EXIT_SLIP_TOOLTIP_ID}
        aria-label={`${count} exit ${count === 1 ? 'ticket' : 'tickets'} done in this unit`}
        className={`${containerClassName} tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0`}
      >
        {badgeBody}
      </button>
      <OverlayPanel
        ref={tooltip}
        id={EXIT_SLIP_TOOLTIP_ID}
        onShow={() => setTooltipOpen(true)}
        onHide={() => setTooltipOpen(false)}
        pt={{ content: { className: '!tw-p-0' } }}
      >
        <div className="tw-flex tw-max-w-[278px] tw-flex-col tw-items-center tw-rounded-xl tw-bg-[#fefbf9] tw-p-6">
          <p className="tw-m-0 tw-text-center tw-font-atkinson tw-text-lg tw-text-[#393939]">
            Total <span className="tw-font-bold">Exit tickets</span> done
          </p>
        </div>
      </OverlayPanel>
    </>
  );
};
